import type { CostRate, CostsByPerson, YmKey } from "@shared/types";
import { apiFetch } from "./api";

async function readJson<T>(res: Response): Promise<T> {
  if (!res.ok) {
    let body: unknown = null;
    try {
      body = await res.json();
    } catch {
      // ignore — body might not be JSON
    }
    const err = new Error(`api ${res.status}`) as Error & { status: number; body: unknown };
    err.status = res.status;
    err.body = body;
    throw err;
  }
  return (await res.json()) as T;
}

/**
 * Fetch the flat cost-rate chain for a single person. Empty array when the
 * person has no rates on file. Returns 403/404 etc. as thrown errors with
 * the response body attached.
 */
export async function getCostRates(personId: number): Promise<CostRate[]> {
  const res = await apiFetch(`/api/people/${personId}/cost-rates`);
  return readJson<CostRate[]>(res);
}

export async function createCostRate(
  personId: number,
  body: {
    projectId: number | null;
    effectiveFrom: YmKey | null;
    validTo: YmKey | null;
    rate: number;
  },
): Promise<CostRate> {
  const res = await apiFetch(`/api/people/${personId}/cost-rates`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  });
  return readJson<CostRate>(res);
}

export async function patchCostRate(
  personId: number,
  rateId: number,
  body: Partial<{
    projectId: number | null;
    effectiveFrom: YmKey | null;
    validTo: YmKey | null;
    rate: number;
  }>,
): Promise<CostRate> {
  const res = await apiFetch(`/api/people/${personId}/cost-rates/${rateId}`, {
    method: "PATCH",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  });
  return readJson<CostRate>(res);
}

export async function deleteCostRate(personId: number, rateId: number): Promise<void> {
  const res = await apiFetch(`/api/people/${personId}/cost-rates/${rateId}`, {
    method: "DELETE",
  });
  if (!res.ok && res.status !== 204) {
    throw new Error(`api ${res.status}`);
  }
}

/**
 * Fetch and pivot cost rates for the given set of people into the CostsByPerson
 * map shape consumers expect (`{ [personId]: CostRate[] }`). At internal-tool
 * scale (~10 users / ~3 concurrent) the N concurrent requests are fine; no
 * bulk endpoint exists.
 */
export async function loadCostsForPeople(personIds: readonly number[]): Promise<CostsByPerson> {
  const out: CostsByPerson = {};
  // allSettled so a single flaky person fetch doesn't drop the rest of the
  // map. 403/404 stay absent silently; other failures log and proceed.
  const results = await Promise.allSettled(
    personIds.map(async (id) => {
      const rows = await getCostRates(id);
      return { id, rows };
    }),
  );
  for (const r of results) {
    if (r.status === "fulfilled") {
      const { id, rows } = r.value;
      out[String(id)] = Array.isArray(rows) ? rows : [];
      continue;
    }
    const status = (r.reason as { status?: number }).status;
    if (status !== 403 && status !== 404) {
      // Surface but don't throw — partial data is better than none.
      console.warn("loadCostsForPeople: fetch failed", r.reason);
    }
  }
  return out;
}
